// @flow

import React, { useContext, useState } from 'react';
import {
  TABLEFLOW_RESOURCES_GRID_FIELDNAMES,
  TABLEFLOW_RESOURCES_GRID_HEADERS,
} from '@streaming-projects/orgs/enums';
import { StyledContainer } from '@src/common-utils/styledComponents';
import { ConfirmModal } from '@presentational/modals/ConfirmModal';
import { DataGrid } from '@components/views/grid-utils/DataGrid';
import {
  useAddTableFlowWorkloadMutation,
  useGetAvailableTableFlowResourcesQuery,
} from '@streaming-projects/service-definitions/streamingProjectsApi';
import { handleGridRowSelectionChanged } from '@src/common-utils/utils';
import { useHistory, useParams } from 'react-router-dom';
import { SPStreamingProjectContext } from '@streaming-projects/sp-page/contexts/SPStreamingProjectContext';
import { toastError } from '@presentational/notifications/utils';
import { getLinkForStreamingProjectsTableFlowWorkloadsPage } from '@streaming-projects/links';
import {
  getMappedDataValueGetter,
  getRegionName,
} from '@streaming-projects/resource-definitions/auxilary-components/MappedDataRenderer';
import { SPMetaDataContext } from '@streaming-projects/orgs/contexts/SPMetaDataContext';
import { ADD_NEW_WORKLOAD_MESSAGE, NO_FREE_RESOURCE_MESSAGE } from '@streaming-projects/constants';
import { ResourceSelectionRadioButtonRenderer } from '@components/views/grid-utils/renderers';
import { QueryError } from '@presentational/QueryError';

export const SPTableFlowWorkloadAddModal = ({ isOpen, setOpen }) => {
  const { orgId, spId } = useParams();
  const { providerRegionsMap, providerNameLabelMap, tableFlowStorageTypeMap } =
    useContext(SPMetaDataContext).metaData;
  const [addTableFlowWorkload] = useAddTableFlowWorkloadMutation();
  const { push } = useHistory();

  const spStreamingProjectData = useContext(SPStreamingProjectContext);

  const [selectedRow, setSelectedRow] = useState(null);
  const columnDefs = [
    {
      field: 'Selection',
      sortable: false,
      filter: false,
      cellRenderer: (props) =>
        ResourceSelectionRadioButtonRenderer(
          props,
          selectedRow,
          TABLEFLOW_RESOURCES_GRID_HEADERS.ID
        ),
    },
    {
      field: TABLEFLOW_RESOURCES_GRID_HEADERS.ID,
      backEndFieldName: TABLEFLOW_RESOURCES_GRID_FIELDNAMES.ID,
      hide: true,
    },
    {
      field: TABLEFLOW_RESOURCES_GRID_HEADERS.NAME,
      backEndFieldName: TABLEFLOW_RESOURCES_GRID_FIELDNAMES.NAME,
    },
    {
      field: TABLEFLOW_RESOURCES_GRID_HEADERS.STORAGE_TYPE,
      backEndFieldName: TABLEFLOW_RESOURCES_GRID_FIELDNAMES.STORAGE_TYPE,
      valueGetter: getMappedDataValueGetter(tableFlowStorageTypeMap),
    },
    {
      field: TABLEFLOW_RESOURCES_GRID_HEADERS.CLUSTER_RESOURCE_NAME,
      backEndFieldName: TABLEFLOW_RESOURCES_GRID_FIELDNAMES.CLUSTER_RESOURCE_NAME,
    },
    {
      field: TABLEFLOW_RESOURCES_GRID_HEADERS.CLUSTER_RESOURCE_PROVIDER,
      backEndFieldName: TABLEFLOW_RESOURCES_GRID_FIELDNAMES.CLUSTER_RESOURCE_PROVIDER,
      valueGetter: getMappedDataValueGetter(providerNameLabelMap),
    },
    {
      field: TABLEFLOW_RESOURCES_GRID_HEADERS.CLUSTER_RESOURCE_REGION,
      backEndFieldName: TABLEFLOW_RESOURCES_GRID_FIELDNAMES.CLUSTER_RESOURCE_REGION,
      tooltipValueGetter: (params) => getRegionName(params, providerRegionsMap),
      valueGetter: (params) => getRegionName(params, providerRegionsMap),
    },
  ];

  const onSelectionChanged = handleGridRowSelectionChanged(
    (rowData) => setSelectedRow(rowData),
    () => setSelectedRow(null)
  );

  // Note: We cannot use DataFetcherComponent here as we need more control
  const { currentData, error, isLoading, isFetching } = useGetAvailableTableFlowResourcesQuery(
    { orgId, spId },
    {
      refetchOnMountOrArgChange: true,
      skip: !orgId || !isOpen,
    }
  );

  if (isLoading || isFetching || !isOpen) {
    return null;
  }

  if (error) {
    return <QueryError error={error} />;
  }

  const resourcesToUse = currentData?.resources?.tableflow_resources ?? [];

  return (
    <StyledContainer>
      <ConfirmModal
        body={
          <StyledContainer>
            <DataGrid
              columnDefs={columnDefs}
              inputDataSource={resourcesToUse}
              noDataMessage={NO_FREE_RESOURCE_MESSAGE}
              onSelectionChanged={onSelectionChanged}
              rowSelection="single"
              sizeColumnsToFitInitially={true}
              tooltipShowDelay={0}
            />
          </StyledContainer>
        }
        cancelButtonNegative={true}
        disabled={!selectedRow}
        fullScreen={true}
        header={ADD_NEW_WORKLOAD_MESSAGE}
        isOpen={isOpen}
        okButtonNegative={false}
        okButtonText="Continue"
        onClickHandlerForCancel={() => {
          setSelectedRow(null);
          setOpen(false);
        }}
        onClickHandlerForOK={async () => {
          const { error, data } = await addTableFlowWorkload({
            orgId,
            spId,
            payload: {
              name: 'Default',
              tableflow_resource_id: selectedRow[TABLEFLOW_RESOURCES_GRID_HEADERS.ID],
              sp_version: spStreamingProjectData?.sp?.version,
            },
          });
          if (error) {
            toastError(error);
          } else {
            push(
              getLinkForStreamingProjectsTableFlowWorkloadsPage(
                orgId,
                spId,
                data.tableflow_detail_id
              )
            );
          }
          setOpen(false);
        }}
      />
    </StyledContainer>
  );
};
